import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SEO from "../components/SEO";
import Header from "../components/Header";
import FiltersBar from "../components/FiltersBar";
import TickerTape from "../components/TickerTape";
import GaugeMeter from "../components/GaugeMeter";
import SentimentBreakdown from "../components/SentimentBreakdown";
import Heatmap from "../components/Heatmap";
import NewsFeed from "../components/NewsFeed";
import AISummary from "../components/AISummary";
import TradingViewWidget from "../components/TradingViewWidget";
import WatchlistManager, { loadWatchlist, saveWatchlist } from "../components/WatchlistManager";
import AlertsPanel from "../components/AlertsPanel";
import ShareSnapshot from "../components/ShareSnapshot";
import Sparkline from "../components/Sparkline";
import RegionalNewsSummary from "../components/RegionalNewsSummary";
import { fetchOverview, fetchHeatmap, fetchTickers, fetchNews, aiSummary, fetchRegionalNewsSummary } from "../lib/api";
import { toTradingViewSymbol, toTradingViewInterval } from "../lib/symbols";

const POLL_MS = 20000;
const TREND_KEY = "gmsm_trend_v1";
const TREND_MAX = 50;

function loadTrend() {
  try {
    return JSON.parse(localStorage.getItem(TREND_KEY) || "{}");
  } catch {
    return {};
  }
}
function saveTrend(t) {
  try {
    localStorage.setItem(TREND_KEY, JSON.stringify(t));
  } catch {}
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [region, setRegion] = useState("Global");
  const [assetClass, setAssetClass] = useState("indices");
  const [overview, setOverview] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [tickers, setTickers] = useState([]);
  const [news, setNews] = useState({ items: [], summary: null });
  const [summaryText, setSummaryText] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selected, setSelected] = useState("^GSPC");
  const [watchlist, setWatchlist] = useState(loadWatchlist());
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState(loadTrend());
  const [regionalSummary, setRegionalSummary] = useState(null);
  const [regionalLoading, setRegionalLoading] = useState(false);
  const lastScoreRef = useRef(null);

  const trendKey = `${region}::${assetClass}`;
  const trendValues = (trend[trendKey] || []).map((p) => p.v);

  const updateWatchlist = (list) => {
    setWatchlist(list);
    saveWatchlist(list);
  };

  const pushAlert = (a) => setAlerts((prev) => [a, ...prev].slice(0, 12));

  const loadAll = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const [ov, hm, tk, nw] = await Promise.all([
        fetchOverview(region, assetClass).catch(() => null),
        fetchHeatmap(region, assetClass).catch(() => null),
        fetchTickers(region, assetClass).catch(() => ({ tickers: [] })),
        fetchNews(region).catch(() => ({ items: [], summary: null })),
      ]);

      if (ov) {
        // Threshold alert
        const prev = lastScoreRef.current;
        const cur = ov.composite;
        if (prev !== null) {
          const wasBull = prev >= 70, wasBear = prev < 30;
          const isBull = cur >= 70, isBear = cur < 30;
          if (!wasBull && isBull) {
            toast.success(`Bullish threshold crossed (${Math.round(cur)})`, { description: `${region} · ${assetClass}` });
            pushAlert({ direction: "up", label: `${region}·${assetClass}`, msg: `Crossed into Bullish (${Math.round(cur)})`, time: new Date().toLocaleTimeString() });
          } else if (!wasBear && isBear) {
            toast.error(`Bearish threshold crossed (${Math.round(cur)})`, { description: `${region} · ${assetClass}` });
            pushAlert({ direction: "down", label: `${region}·${assetClass}`, msg: `Dropped into Bearish (${Math.round(cur)})`, time: new Date().toLocaleTimeString() });
          } else if (Math.abs(cur - prev) >= 8) {
            const dir = cur > prev ? "up" : "down";
            toast(`Sentiment ${dir === "up" ? "↑" : "↓"} ${Math.abs(cur - prev).toFixed(1)} pts`, { description: `${region} · ${assetClass} now ${Math.round(cur)}` });
            pushAlert({ direction: dir, label: `${region}·${assetClass}`, msg: `${dir === "up" ? "Spiked up" : "Dropped"} ${Math.abs(cur - prev).toFixed(1)} pts`, time: new Date().toLocaleTimeString() });
          }
        }
        lastScoreRef.current = cur;
        setOverview(ov);
        // append to trend history (in-memory + localStorage), de-dup if same minute
        setTrend((prev) => {
          const k = `${ov.region}::${ov.asset_class}`;
          const existing = prev[k] || [];
          const now = Math.floor(Date.now() / 1000);
          const last = existing[existing.length - 1];
          let next;
          if (last && now - last.t < 30) {
            // replace last sample (avoid duplicate poll spam)
            next = [...existing.slice(0, -1), { t: now, v: cur }];
          } else {
            next = [...existing, { t: now, v: cur }];
          }
          if (next.length > TREND_MAX) next = next.slice(next.length - TREND_MAX);
          const out = { ...prev, [k]: next };
          saveTrend(out);
          return out;
        });
        // Trigger AI summary refresh
        setSummaryLoading(true);
        aiSummary({
          region: ov.region,
          asset_class: ov.asset_class,
          composite: ov.composite,
          label: ov.label,
          breakdown: ov.breakdown,
        })
          .then((r) => setSummaryText(r.summary))
          .catch(() => setSummaryText("Markets are showing mixed signals across asset classes."))
          .finally(() => setSummaryLoading(false));
      }
      if (hm) setHeatmap(hm);
      if (tk?.tickers) setTickers(tk.tickers);
      if (nw) setNews(nw);
    } finally {
      setLoading(false);
    }
  }, [region, assetClass]);

  useEffect(() => {
    loadAll(true);
    const id = setInterval(() => loadAll(false), POLL_MS);
    return () => clearInterval(id);
  }, [loadAll]);

  // Regional AI news summary — refresh whenever region changes
  useEffect(() => {
    let cancelled = false;
    setRegionalSummary(null);
    setRegionalLoading(true);
    fetchRegionalNewsSummary(region)
      .then((d) => { if (!cancelled) setRegionalSummary(d); })
      .catch(() => { if (!cancelled) setRegionalSummary(null); })
      .finally(() => { if (!cancelled) setRegionalLoading(false); });
    return () => { cancelled = true; };
  }, [region]);

  // Update default selected based on first asset in heatmap
  useEffect(() => {
    if (heatmap?.cells?.length && !heatmap.cells.find((c) => c.symbol === selected)) {
      setSelected(heatmap.cells[0].symbol);
    }
  }, [heatmap, selected]);

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <SEO
        title="Live Global Market Sentiment Dashboard"
        path="/"
        description="Real-time composite sentiment scores across global stocks, indices, crypto, forex, commodities and bonds. AI-powered news + sector heatmap + live TradingView charts. How does the market feel right now?"
        keywords={["market sentiment", "stock sentiment dashboard", "global markets live", "AI market news", "S&P 500 sentiment", "Nifty sentiment", "crypto sentiment", "trading dashboard"]}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebApplication",
              name: "Sent-i-meter",
              url: "https://sent-i-meter.com",
              applicationCategory: "FinanceApplication",
              operatingSystem: "Web",
              description: "Live global market sentiment dashboard with AI news, sector heatmap and TradingView charts.",
              offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "How is the Sent-i-meter sentiment score calculated?",
                  acceptedAnswer: { "@type": "Answer", text: "A weighted composite: technical signals (RSI, MACD, moving averages) 40%, momentum 20%, news sentiment classified by Claude Sonnet 4.5 30%, and volatility 10%. Scores above 60 are bullish, below 40 bearish, 40–60 neutral." },
                },
                {
                  "@type": "Question",
                  name: "Is Sent-i-meter free?",
                  acceptedAnswer: { "@type": "Answer", text: "Yes. Sent-i-meter is free with no signup or paywall. Watchlists and alerts save to your browser via localStorage." },
                },
                {
                  "@type": "Question",
                  name: "Is the data financial advice?",
                  acceptedAnswer: { "@type": "Answer", text: "No. Sent-i-meter is for informational use only. Do your own research before making any investment decision." },
                },
                {
                  "@type": "Question",
                  name: "Which markets does Sent-i-meter cover?",
                  acceptedAnswer: { "@type": "Answer", text: "USA, India, Europe and Asia equities, major indices, top 30 cryptocurrencies, forex majors, commodities, bonds, and the luxury watch segment." },
                },
              ],
            },
          ],
        }}
      />
      <Header ts={overview?.ts} />
      <FiltersBar region={region} assetClass={assetClass} onRegion={setRegion} onAssetClass={setAssetClass} />
      <TickerTape tickers={tickers} />

      <main className="px-4 md:px-6 py-6 max-w-[1600px] mx-auto">
        {/* Hero row: Gauge + Breakdown + AI */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
          <div className="bg-surface border border-white/10 p-5 lg:col-span-1 grid-bg">
            <div className="flex items-center justify-between mb-2 gap-2">
              <h2 className="heading text-xl font-bold">Composite Sentiment</h2>
              <div className="flex items-center gap-2">
                <span className="overline hidden md:inline">{region} · {assetClass}</span>
                <ShareSnapshot
                  overview={overview}
                  summaryText={summaryText}
                  news={news}
                  region={region}
                  assetClass={assetClass}
                />
              </div>
            </div>
            <div className="flex justify-center mt-2">
              {loading && !overview ? (
                <div className="h-[260px] w-full flex items-center justify-center overline">loading market data...</div>
              ) : (
                <GaugeMeter value={overview?.composite || 0} label={overview?.label || "Loading"} />
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 border-t border-white/10 pt-3">
              <div>
                <div className="overline">range</div>
                <div className="mono text-xs text-bearish">0–30 BEAR</div>
              </div>
              <div>
                <div className="overline">neutral</div>
                <div className="mono text-xs text-neutral_zone">30–70</div>
              </div>
              <div>
                <div className="overline">bull</div>
                <div className="mono text-xs text-bullish">70–100</div>
              </div>
            </div>
            {trendValues.length >= 2 && (
              <div className="mt-3 border-t border-white/10 pt-3" data-testid="composite-trend">
                <div className="flex items-center justify-between mb-1">
                  <span className="overline">trend · last {trendValues.length}</span>
                  <span
                    className="mono text-xs"
                    style={{
                      color:
                        trendValues[trendValues.length - 1] - trendValues[0] >= 0
                          ? "#34C759"
                          : "#FF3B30",
                    }}
                  >
                    {(trendValues[trendValues.length - 1] - trendValues[0]).toFixed(1)}
                  </span>
                </div>
                <Sparkline values={trendValues} width={300} height={36} />
              </div>
            )}
          </div>
          <div className="lg:col-span-1">
            <SentimentBreakdown breakdown={overview?.breakdown} />
          </div>
          <div className="lg:col-span-1 flex flex-col gap-4 md:gap-6">
            <AISummary summary={summaryText} loading={summaryLoading} />
            <RegionalNewsSummary region={region} data={regionalSummary} loading={regionalLoading} />
            <AlertsPanel alerts={alerts} />
          </div>
        </section>

        {/* Heatmap */}
        <section className="mb-6">
          <Heatmap
            cells={heatmap?.cells || []}
            groupBy={assetClass === "stocks" ? "sector" : null}
            onSelect={(s) => navigate(`/asset/${encodeURIComponent(s)}`)}
          />
        </section>

        {/* Chart + News + Watchlist */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
          <div className="lg:col-span-2">
            <TradingViewWidget symbol={toTradingViewSymbol(selected)} interval={toTradingViewInterval(selected)} height={460} />
          </div>
          <div className="lg:col-span-1 flex flex-col gap-4 md:gap-6">
            <WatchlistManager
              items={watchlist}
              onChange={updateWatchlist}
              onSelect={(s) => navigate(`/asset/${encodeURIComponent(s)}`)}
            />
            <NewsFeed items={news.items} summary={news.summary} />
          </div>
        </section>

        {/* SEO / FAQ section — visible to crawlers + new users */}
        <section className="mt-10 border-t border-white/10 pt-8" data-testid="seo-section">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
            <div>
              <div className="overline mb-2">about</div>
              <h2 className="heading text-xl font-bold tracking-tight">What is Sent-i-meter?</h2>
              <p className="text-sm text-white/70 mt-3 leading-relaxed">
                Sent-i-meter is a live <strong>global market sentiment dashboard</strong> that scores every stock,
                index, currency, commodity, crypto and luxury watch on a 0–100 composite scale. It blends
                <strong> technical signals</strong> (RSI, MACD, moving averages), <strong>momentum</strong> over multiple
                horizons, <strong>news sentiment</strong> classified by Claude Sonnet 4.5, and <strong>volatility</strong>.
                Updated continuously, no signup required.
              </p>
            </div>
            <div>
              <div className="overline mb-2">popular pages</div>
              <h2 className="heading text-xl font-bold tracking-tight">Where to start</h2>
              <ul className="mt-3 space-y-2 text-sm">
                <li><a href="/most-bullish" className="text-neutral_zone hover:text-yellow-300">Most bullish assets today →</a></li>
                <li><a href="/most-bearish" className="text-neutral_zone hover:text-yellow-300">Most bearish assets today →</a></li>
                <li><a href="/sectors" className="text-neutral_zone hover:text-yellow-300">Sentiment by sector →</a></li>
                <li><a href="/insights" className="text-neutral_zone hover:text-yellow-300">Daily AI market briefings →</a></li>
                <li><a href="/watches" className="text-neutral_zone hover:text-yellow-300">Luxury watch segment index →</a></li>
                <li><a href="/compare/AAPL-vs-MSFT" className="text-neutral_zone hover:text-yellow-300">Compare AAPL vs MSFT →</a></li>
              </ul>
            </div>
            <div>
              <div className="overline mb-2">faq</div>
              <h2 className="heading text-xl font-bold tracking-tight">Common questions</h2>
              <dl className="mt-3 space-y-3 text-sm">
                <div>
                  <dt className="font-semibold text-white">How is the sentiment score calculated?</dt>
                  <dd className="text-white/70 mt-1">A weighted composite: technical signals 40%, momentum 20%, news sentiment 30%, volatility 10%. Scores above 60 are bullish, below 40 bearish, 40–60 neutral.</dd>
                </div>
                <div>
                  <dt className="font-semibold text-white">Is it free?</dt>
                  <dd className="text-white/70 mt-1">Yes. Sent-i-meter is free with no signup or paywall. Watchlists and alerts save to your browser.</dd>
                </div>
                <div>
                  <dt className="font-semibold text-white">Is this financial advice?</dt>
                  <dd className="text-white/70 mt-1">No. Sent-i-meter is for informational use only. Do your own research before making any investment decision.</dd>
                </div>
                <div>
                  <dt className="font-semibold text-white">Which markets are covered?</dt>
                  <dd className="text-white/70 mt-1">USA, India, Europe, Asia equities, major indices, top 30 cryptocurrencies, forex majors, commodities, bonds, and the luxury watch segment.</dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <footer className="border-t border-white/10 pt-4 mt-8 flex items-center justify-between">
          <span className="overline">© sent-i-meter · for informational use</span>
          <span className="overline">data: yfinance · coingecko · google news · claude sonnet 4.5</span>
        </footer>
      </main>
    </div>
  );
}
